const { getUnknownBatchID } = require('../shipment/tools/virtual-batch')

const DEFAULT_BATCH_SUFFIX = ':manufacturer:unknown:batchNo:UNKNOWN'

function batchTrackingFromUser (user) {
  const roles = (user && user.roles) || []
  return roles.some(role => role === 'feature:batchTracking')
}

function swapOriginDest (shipment) {
  return { origin: shipment.destination, destination: shipment.origin }
}

function joinRemarks (remarksList) {
  return (remarksList || [])
    .filter(Boolean)
    .join(', ')
}

function buildSnapshotBase (originalShipment, date, comment) {
  const { origin, destination } = swapOriginDest(originalShipment)
  return {
    origin,
    destination,
    date,
    shipmentTypeId: 'routine',
    comment,
    isAutomaticReturnShipment: true,
    parentDocId: originalShipment.id,
    orderId: originalShipment.orderId
  }
}

// Key builders
function keyUnknownBatch (skuCode) {
  return getUnknownBatchID(skuCode)
}

function keySkuSupplier (skuCode, supplierRef) {
  return `product:${skuCode}:manufacturer:${supplierRef}`
}

function slug (s) {
  return String(s || '')
    .toLowerCase()
    .replace(/\s+/g, '_')
}

function keySkuBatch (skuDesc, batchNo) {
  return `product:${slug(skuDesc)}:manufacturer:UNKNOWN:batchNo:${batchNo}`
}

// Quantity selectors
function qtyRtv (item) {
  return (item.rtv_quantity || 0)
}

function qtySalesReturn (item) {
  return (item.accepted_quantity || 0) + (item.sales_return_quantity || 0)
}

/**
 * Extract country code from location ID
 * @param {string} locationId - Location ID in format 'country:XX:state:YY' or similar
 * @returns {string|null} - Country code (e.g., 'ng', 'ke') or null if not found
 */
function extractCountryCode (locationId) {
  if (!locationId || typeof locationId !== 'string') {
    return null
  }

  // Match pattern like 'country:ng:state:fct' or 'country:ke:state:nairobi'
  const match = locationId.match(/^country:([a-z]{2})/)
  return match ? match[1] : null
}

/**
 * Sum counts for either flat list of items or list of entries with items.
 * options: { nested: boolean, key: (item)=>string, qty: (item)=>number, enforceNonNull: boolean }
 */
function sumCounts (source, { nested = false, key, qty, enforceNonNull = false } = {}) {
  const counts = {}
  const eachItem = (item) => {
    if (!item) {
      if (enforceNonNull) {
        throw new Error('undefined-item-inside-rtv')
      }
      return
    }
    const k = key(item)
    const amount = qty(item)
    if (!counts[k]) counts[k] = { quantity: 0 }
    counts[k].quantity += amount
  }

  if (nested) {
    (source || []).forEach(entry => (entry.items || []).forEach(eachItem))
  } else {
    (source || []).forEach(eachItem)
  }
  return counts
}

function aggregateQuantitiesBySku (items = [], qtySelector = (() => 0)) {
  const totals = {}
  items.forEach(item => {
    if (!item || typeof item.sku_code === 'undefined') {
      return
    }
    const qty = Number(qtySelector(item))
    if (!Number.isFinite(qty)) {
      return
    }
    totals[item.sku_code] = (totals[item.sku_code] || 0) + qty
  })
  return totals
}

function updateCountsFromSkuQuantities (counts, skuQuantities = {}) {
  if (!skuQuantities || Object.keys(skuQuantities).length === 0) {
    return counts
  }

  const baseCounts = counts || {}
  const updatedCounts = { ...baseCounts }
  const matchedSkus = new Set()

  Object.entries(baseCounts).forEach(([countKey, entry]) => {
    const match = (countKey || '').match(/^product:([^:]+)/i)
    if (!match) return
    const skuCode = match[1]

    if (Object.prototype.hasOwnProperty.call(skuQuantities, skuCode)) {
      updatedCounts[countKey] = { ...(entry || {}), quantity: skuQuantities[skuCode] }
      matchedSkus.add(skuCode)
    }
  })

  Object.entries(skuQuantities).forEach(([skuCode, quantity]) => {
    if (matchedSkus.has(skuCode)) return
    const fallbackKey = `product:${skuCode}${DEFAULT_BATCH_SUFFIX}`
    updatedCounts[fallbackKey] = { quantity }
  })

  return updatedCounts
}

module.exports = {
  batchTrackingFromUser,
  swapOriginDest,
  joinRemarks,
  buildSnapshotBase,
  keyUnknownBatch,
  keySkuSupplier,
  keySkuBatch,
  qtyRtv,
  qtySalesReturn,
  sumCounts,
  extractCountryCode,
  aggregateQuantitiesBySku,
  updateCountsFromSkuQuantities
}
